import React, { useEffect, useRef, useState } from 'react';

import { cssMap } from '@atlaskit/css';
import AiChatIcon from '@atlaskit/icon/core/ai-chat';
import { Box, Pressable, Stack, Text } from '@atlaskit/primitives/compiled';
import { AgentAvatar } from '@atlaskit/rovo-agent-components';
import Skeleton from '@atlaskit/skeleton';
import { token } from '@atlaskit/tokens';

const styles = cssMap({
	outerContainerStyles: {
		backgroundColor: token('color.background.neutral.subtle'),
		borderRadius: token('radius.large'),
		paddingLeft: token('space.200'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.050'),
		width: '100%',
	},

	containerStyles: {
		borderRadius: token('radius.large'),
		borderWidth: token('border.width'),
		borderColor: token('color.border'),
		borderStyle: 'solid',
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
			borderRadius: token('radius.large'),
			boxSizing: 'border-box',
		},
	},

	skeletonContainerStyles: {
		borderRadius: token('radius.large'),
		borderWidth: token('border.width'),
		borderColor: token('color.border'),
		borderStyle: 'solid',
	},

	contentStyles: {
		paddingTop: token('space.150'),
		paddingRight: token('space.150'),
		paddingBottom: token('space.150'),
		paddingLeft: token('space.150'),
		display: 'flex',
	},

	promptTextStyles: {
		maxWidth: '100%',
	},

	agentInfoStyles: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.050'),
	},
});

interface ConvoStarterItemProps {
	agentInfo?: {
		id?: string;
		identityAccountId?: string;
		name?: string;
		namedId?: string;
	};
	icon: React.ReactElement;
	isSelected: boolean;
	onClick?: () => void;
	title: string;
	type: string;
}

interface ConvoStarterSkeletonProps {
	numberOfItems: number;
}

export function ConvoStarterItem({
	title,
	icon,
	isSelected,
	onClick,
	type,
	agentInfo,
}: ConvoStarterItemProps) {
	const ref = useRef<HTMLDivElement>(null);
	const [isHovered, setIsHovered] = useState(false);

	useEffect(() => {
		if (isSelected && ref.current) {
			ref.current.scrollIntoView({
				behavior: 'instant',
				block: 'nearest',
			});
		}
	}, [isSelected, ref]);

	return (
		<Pressable
			onClick={onClick}
			xcss={styles.outerContainerStyles}
			onMouseEnter={() => setIsHovered(true)}
			onMouseLeave={() => setIsHovered(false)}
		>
			<Box xcss={styles.containerStyles} ref={ref}>
				<Stack xcss={styles.contentStyles} alignInline="start" space="space.050" grow="fill">
					{icon}
					<Box xcss={styles.promptTextStyles}>
						<Text maxLines={3} align="start">
							{title}
						</Text>
					</Box>
					{agentInfo && isHovered && (
						<Box xcss={styles.agentInfoStyles}>
							<Text align="start" color="color.text.subtlest" size="small">
								By
							</Text>
							<AgentAvatar
								agentId={agentInfo.id}
								agentNamedId={agentInfo.namedId}
								agentIdentityAccountId={agentInfo.identityAccountId}
								size="xsmall"
							/>
							<Text align="start" color="color.text.subtlest" size="small">
								{agentInfo.name}
							</Text>
						</Box>
					)}
				</Stack>
			</Box>
		</Pressable>
	);
}

export function ConvoStarterSkeleton({ numberOfItems }: ConvoStarterSkeletonProps) {
	return (
		<>
			{Array.from({ length: numberOfItems }).map((_, index) => (
				<Box key={index} xcss={styles.outerContainerStyles}>
					<Box xcss={styles.skeletonContainerStyles}>
						<Stack xcss={styles.contentStyles} space="space.050">
							<AiChatIcon label="" color={token('color.text.disabled')} />
							<Skeleton width="100%" height={16} isShimmering={true} />
							<Skeleton width="75%" height={16} isShimmering={true} />
						</Stack>
					</Box>
				</Box>
			))}
		</>
	);
}
